import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, CheckCircle, Mail, PawPrint, Sparkles, ChevronRight } from 'lucide-react';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ── Questions ──
const QUESTIONS = [
  {
    id: 'q1', part: 1, partLabel: 'Your Dog\'s Nature',
    question: 'When your dog walks into a new environment, a friend\'s house, a busy park, a vet\'s office, their natural energy is:',
    options: [
      { label: 'A', text: 'Confident. They survey the space, decide who\'s who, take their position.', maps: 'Director' },
      { label: 'B', text: 'Easy. They roll with it, find a comfortable spot, adapt to the energy.', maps: 'Peacemaker' },
      { label: 'C', text: 'Heightened. They scan, react to sounds, stay close to you or hold back.', maps: 'Empath' },
    ]
  },
  {
    id: 'q2', part: 1, partLabel: 'Your Dog\'s Nature',
    question: 'When you correct your dog or hold a boundary, their honest response is:',
    options: [
      { label: 'A', text: 'They push back. They test. They watch to see if you really mean it.', maps: 'Director' },
      { label: 'B', text: 'They soften. They give in without losing their connection to you.', maps: 'Peacemaker' },
      { label: 'C', text: 'They shut down or get anxious. They\'ve been reading your tone before the words.', maps: 'Empath' },
    ]
  },
  {
    id: 'q3', part: 1, partLabel: 'Your Dog\'s Nature',
    question: 'When you watch your dog with people or other dogs, the dominant pattern you see is:',
    options: [
      { label: 'A', text: 'They try to lead. They posture, set the pace, take charge.', maps: 'Director' },
      { label: 'B', text: 'They get along with everyone easily, they adapt to whoever they\'re with.', maps: 'Peacemaker' },
      { label: 'C', text: 'They get overwhelmed. Big feelings. Either reactive or shut down.', maps: 'Empath' },
    ]
  },
  {
    id: 'q4', part: 2, partLabel: 'Your Pattern',
    question: 'A thunderstorm hits. Your dog is pacing, panting, shaking. Without thinking, your first instinct is to:',
    options: [
      { label: 'A', text: 'Get on the floor with them, hold them, talk to them softly.', maps: 'Comforter' },
      { label: 'B', text: 'Watch them anxiously, mentally running through what could go wrong.', maps: 'Worrier' },
      { label: 'C', text: 'Tell them firmly to settle and expect them to understand what you mean.', maps: 'Frustrated Leader' },
      { label: 'D', text: 'Try to comfort them while still finishing what you were doing, you\'re already running behind.', maps: 'Overwhelmed Caregiver' },
      { label: 'E', text: 'Reach for whatever you\'ve seen "good dog parents" do, even if it doesn\'t feel like you.', maps: 'Performer' },
    ]
  },
  {
    id: 'q5', part: 2, partLabel: 'Your Pattern',
    question: 'Your dog snatches food off the kitchen counter. Without missing a beat, your reaction is:',
    options: [
      { label: 'A', text: '"Oh sweetie, no!", half-laughing, half-apologetic.', maps: 'Comforter' },
      { label: 'B', text: 'Tense and embarrassed, like you got caught failing at this.', maps: 'Worrier' },
      { label: 'C', text: 'Sharp correction. Raise your voice. Wonder why they STILL do this.', maps: 'Frustrated Leader' },
      { label: 'D', text: 'Sigh. Clean it up. Add it to the running list of things gone wrong today.', maps: 'Overwhelmed Caregiver' },
      { label: 'E', text: 'Wonder what a "real" dog owner would have done differently.', maps: 'Performer' },
    ]
  },
  {
    id: 'q6', part: 2, partLabel: 'Your Pattern',
    question: 'Try not to censor this one. When you think about your relationship with your dog, the feeling that comes up most often is:',
    options: [
      { label: 'A', text: '"I love them so much, I worry I\'m not enough."', maps: 'Comforter' },
      { label: 'B', text: '"I\'m always scared something will go wrong."', maps: 'Worrier' },
      { label: 'C', text: '"I get frustrated more than I\'d like to admit."', maps: 'Frustrated Leader' },
      { label: 'D', text: '"I love them, but most days I\'m just trying to keep up."', maps: 'Overwhelmed Caregiver' },
      { label: 'E', text: '"I want to be the kind of owner my dog deserves."', maps: 'Performer' },
    ]
  },
  {
    id: 'q7', part: 2, partLabel: 'Your Pattern',
    question: 'Your dog is reactive on walks, pulling, barking, lunging. Pause on this, the honest truth about why you think it\'s happening:',
    options: [
      { label: 'A', text: '"They\'ve had hard experiences. I need to protect them."', maps: 'Comforter' },
      { label: 'B', text: '"I\'m probably making it worse with my anxiety."', maps: 'Worrier' },
      { label: 'C', text: '"They need better training. I haven\'t been firm enough."', maps: 'Frustrated Leader' },
      { label: 'D', text: '"I don\'t have the bandwidth to figure it out right now. I just need it to stop."', maps: 'Overwhelmed Caregiver' },
      { label: 'E', text: '"I keep wondering what a good trainer would do."', maps: 'Performer' },
    ]
  },
  {
    id: 'q8', part: 2, partLabel: 'Your Pattern',
    question: 'When you imagine the dog you wish you had, the version you\'re working toward, you picture:',
    options: [
      { label: 'A', text: 'A dog who\'s calm, affectionate, and never anxious or afraid.', maps: 'Comforter' },
      { label: 'B', text: 'A dog who\'s predictable, so you don\'t have to worry about what\'s next.', maps: 'Worrier' },
      { label: 'C', text: 'A dog who listens the first time. Every time.', maps: 'Frustrated Leader' },
      { label: 'D', text: 'A dog who fits into your life without making it harder.', maps: 'Overwhelmed Caregiver' },
      { label: 'E', text: 'A dog you can take anywhere and feel proud of.', maps: 'Performer' },
    ]
  },
  {
    id: 'q9', part: 3, partLabel: 'One Last Thing',
    question: 'The real reason you\'re taking this quiz today is:',
    optional: true,
    options: [
      { label: 'A', text: 'Something is off and I can\'t name what.' },
      { label: 'B', text: 'My dog is reactive, anxious, or struggling.' },
      { label: 'C', text: 'I love my dog, but I feel like I\'m failing them.' },
      { label: 'D', text: 'I\'m tired and I want a relationship that feels easier.' },
      { label: 'E', text: 'I\'m curious what this framework reveals.' },
    ]
  },
];

// ── Result Content ──
const DOG_NATURES = {
  Director: {
    title: 'The Director',
    tagline: 'Your dog was born to lead.',
    description: 'Directors are confident, decisive, and always scanning for who\'s in charge. They don\'t need reassurance, they need a leader they can trust. When they don\'t have one, they take the job themselves. That\'s when the pulling, the door-dashing, the ignoring starts. It\'s not defiance. It\'s a dog filling a vacuum.',
    strengths: 'Confident, focused, driven, protective, loyal once earned',
    challenge: 'Your Director doesn\'t need softness, they need structure. Clear, calm, consistent. They\'re testing you because they want to trust you.',
  },
  Peacemaker: {
    title: 'The Peacemaker',
    tagline: 'Your dog reads the room before they enter it.',
    description: 'Peacemakers are adaptable, easygoing, and tuned into the social energy of any space. They\'re the dog who gets along with everyone, but that doesn\'t mean they don\'t need leadership. Peacemakers avoid conflict, which means they\'ll tolerate bad habits until the pressure builds. Then you get a dog that seems "fine" until they\'re suddenly not.',
    strengths: 'Calm, social, adaptable, forgiving, easy to bond with',
    challenge: 'Your Peacemaker doesn\'t need volume, they need clarity. They\'ll follow anyone who leads with steady, calm energy.',
  },
  Empath: {
    title: 'The Empath',
    tagline: 'Your dog feels everything you feel, before you feel it.',
    description: 'Empaths are sensitive, reactive, and deeply bonded to their person. They don\'t just read energy, they absorb it. When you\'re stressed, they escalate. When you\'re calm, they settle. This is the dog who hides during thunderstorms, gets anxious when you leave, or reacts to everything on a walk. It\'s not a training problem, it\'s an energy problem.',
    strengths: 'Deeply loyal, intuitive, responsive, emotionally intelligent',
    challenge: 'Your Empath doesn\'t need discipline, they need regulation. Yours first, then theirs.',
  },
};

const HUMAN_PATTERNS = {
  Comforter: {
    title: 'The Comforter',
    tagline: 'You lead with love. Your dog needs you to lead with structure first.',
    description: 'You feel everything your dog feels, and your instinct is to soothe it. Comfort isn\'t wrong, but when it\'s your only tool, your dog learns that anxiety gets rewarded and big feelings get matched with more feelings. The work for you isn\'t to stop caring. It\'s to care and hold the boundary at the same time.',
  },
  Worrier: {
    title: 'The Worrier',
    tagline: 'You see every risk before it arrives. Your dog feels that.',
    description: 'You\'re always one step ahead, anticipating the bark, the lunge, the thing that could go wrong. The problem is, your dog reads your tension as confirmation that the world is dangerous. You don\'t need to stop worrying. You need to learn to regulate your body so your dog stops reading your fear as a signal.',
  },
  'Frustrated Leader': {
    title: 'The Frustrated Leader',
    tagline: 'You know what you want. You just don\'t know why it isn\'t working.',
    description: 'You expect results. You\'re consistent, until you\'re not, because nothing\'s changing fast enough. The frustration builds. Your dog feels it before you express it, and they shut down or push back harder. The work for you isn\'t more firmness, it\'s patience. Slowing down is harder than pushing through. That\'s the lesson.',
  },
  'Overwhelmed Caregiver': {
    title: 'The Overwhelmed Caregiver',
    tagline: 'You\'re holding everything together. Your dog is on the list, but so is everything else.',
    description: 'You love your dog, but you\'re running on empty. Training feels like one more thing on a list that never ends. The walk gets skipped. The boundary slides. Not because you don\'t care, because you\'re surviving. The work for you isn\'t more effort. It\'s permission to do less, better. Three minutes of structure beats thirty minutes of chaos.',
  },
  Performer: {
    title: 'The Performer',
    tagline: 'You want to do it right. That\'s the trap.',
    description: 'You\'re always watching what other dog owners do. You\'ve consumed the content, bought the tools, tried the methods, and you\'re still not sure you\'re doing it right. The Performer doesn\'t trust their own instincts. The work for you isn\'t more information. It\'s learning to trust what you already know and stop performing for an audience that isn\'t watching.',
  },
};

// ── Scoring Functions ──
function scoreDogNature(answers) {
  const counts = { Director: 0, Peacemaker: 0, Empath: 0 };
  const order = [];
  ['q1', 'q2', 'q3'].forEach(qId => {
    const pick = answers[qId];
    if (pick) {
      const q = QUESTIONS.find(q => q.id === qId);
      const opt = q.options.find(o => o.label === pick);
      if (opt) {
        counts[opt.maps]++;
        if (!order.includes(opt.maps)) order.push(opt.maps);
      }
    }
  });
  const max = Math.max(...Object.values(counts));
  const winners = Object.keys(counts).filter(k => counts[k] === max);
  if (winners.length === 1) return winners[0];
  // Tie: first-picked wins
  return order.find(n => winners.includes(n)) || winners[0];
}

function scoreHumanPattern(answers) {
  const counts = { Comforter: 0, Worrier: 0, 'Frustrated Leader': 0, 'Overwhelmed Caregiver': 0, Performer: 0 };
  const order = [];
  ['q4', 'q5', 'q6', 'q7', 'q8'].forEach(qId => {
    const pick = answers[qId];
    if (pick) {
      const q = QUESTIONS.find(q => q.id === qId);
      const opt = q.options.find(o => o.label === pick);
      if (opt) {
        counts[opt.maps]++;
        if (!order.includes(opt.maps)) order.push(opt.maps);
      }
    }
  });
  const max = Math.max(...Object.values(counts));
  const winners = Object.keys(counts).filter(k => counts[k] === max);
  if (winners.length === 1) return winners[0];
  return order.find(p => winners.includes(p)) || winners[0];
}

function getBestMatchTier(dogNature, humanPattern) {
  if (dogNature === 'Empath') return 'Full Package';
  if (['Comforter', 'Worrier', 'Frustrated Leader'].includes(humanPattern)) return 'Full Package';
  if (humanPattern === 'Overwhelmed Caregiver') return 'Monthly';
  if (humanPattern === 'Performer') return 'Accelerated';
  return 'Full Package';
}

const PRICING = {
  'Full Package': { price: '$675', period: 'one-time', save: 'Save 10%', desc: 'All 10 boxes shipped together. The full transformation, front-loaded.' },
  'Accelerated': { price: '$825', period: 'every 2 weeks', save: null, desc: 'A new box every 2 weeks. For the Performer who wants momentum.' },
  'Monthly': { price: '$75', period: '/month', save: null, desc: 'One box per month. Build at your pace, on your schedule.' },
};

// ── Component ──
export default function QuizPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0); // 0=intro, 1-9=questions, 10=email gate, 11=result
  const [answers, setAnswers] = useState({});
  const [result, setResult] = useState(null);
  const [quizId, setQuizId] = useState(null);
  const [email, setEmail] = useState('');
  const [emailSaved, setEmailSaved] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [pendingAnswers, setPendingAnswers] = useState(null);

  const currentQuestion = step >= 1 && step <= 9 ? QUESTIONS[step - 1] : null;
  const totalQuestions = 9;
  const progress = step >= 1 && step <= 9 ? (step / totalQuestions) * 100 : step > 9 ? 100 : 0;

  const handleAnswer = (questionId, optionLabel) => {
    setAnswers(prev => ({ ...prev, [questionId]: optionLabel }));
    setAnimating(true);
    setTimeout(() => {
      if (step < 9) {
        setStep(step + 1);
      } else {
        // Go to email gate instead of calculating result directly
        setPendingAnswers({ ...answers, [questionId]: optionLabel });
        setStep(10);
      }
      setAnimating(false);
    }, 400);
  };

  const skipOptional = () => {
    setPendingAnswers(answers);
    setStep(10);
  };

  const calculateResult = async (finalAnswers, userEmail) => {
    const dogNature = scoreDogNature(finalAnswers);
    const humanPattern = scoreHumanPattern(finalAnswers);
    const bestMatch = getBestMatchTier(dogNature, humanPattern);

    // Q9 answer text
    let q9Text = null;
    if (finalAnswers.q9) {
      const q9 = QUESTIONS.find(q => q.id === 'q9');
      const opt = q9.options.find(o => o.label === finalAnswers.q9);
      q9Text = opt?.text || null;
    }

    setResult({ dogNature, humanPattern, bestMatch, q9Text });
    setStep(11);

    // Save to DB with email included
    try {
      const res = await axios.post(`${API}/quiz/submit`, {
        dog_nature: dogNature,
        human_pattern: humanPattern,
        best_match_tier: bestMatch,
        q9_answer: q9Text,
        answers: finalAnswers,
        email: userEmail,
      });
      setQuizId(res.data.id);
      setEmailSaved(true);
    } catch (e) {
      console.error('Failed to save quiz:', e);
    }
  };

  const handleEmailGateSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setSubmitting(true);
    try {
      await calculateResult(pendingAnswers, email.trim());
    } finally {
      setSubmitting(false);
    }
  };

  // ── Intro Screen ──
  if (step === 0) {
    return (
      <div className="min-h-screen bg-white">
        <header className="py-4 px-6 flex items-center justify-between max-w-5xl mx-auto">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-500 hover:text-edupaws-navy transition-colors font-body">
            <ArrowLeft className="w-5 h-5" /> Home
          </button>
          <img src="https://edupaws.co/cdn/shop/files/Edu_Paws_Logo_Updated.png?v=1759285352&width=100" alt="Edu Paws" className="h-10" />
        </header>

        <div className="max-w-2xl mx-auto px-6 py-16 text-center">
          <div className="w-20 h-20 bg-edupaws-sage/15 rounded-3xl flex items-center justify-center mx-auto mb-8">
            <PawPrint className="w-10 h-10 text-edupaws-sage" />
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-edupaws-navy mb-6 leading-tight">
            Discover Your Dog's True Nature
          </h1>
          <p className="text-lg text-gray-600 font-body mb-4 leading-relaxed max-w-lg mx-auto">
            9 questions. No fluff. You'll learn your dog's psychological profile
            and the human pattern that's shaping your relationship.
          </p>
          <p className="text-base text-gray-400 font-body mb-10">Takes about 3 minutes</p>

          <button
            onClick={() => setStep(1)}
            data-testid="start-quiz-btn"
            className="inline-flex items-center gap-3 bg-edupaws-navy text-white font-heading font-bold text-xl py-4 px-10 rounded-full
                       shadow-[0_4px_0_#2D3B47] hover:shadow-[0_2px_0_#2D3B47] hover:translate-y-[2px] transition-all"
          >
            Start the Quiz <ArrowRight className="w-5 h-5" />
          </button>

          <div className="mt-12 grid grid-cols-3 gap-6 text-center">
            <div>
              <div className="font-heading text-2xl font-bold text-edupaws-navy">3</div>
              <div className="text-sm text-gray-500 font-body">Dog Nature Questions</div>
            </div>
            <div>
              <div className="font-heading text-2xl font-bold text-edupaws-navy">5</div>
              <div className="text-sm text-gray-500 font-body">Human Pattern Questions</div>
            </div>
            <div>
              <div className="font-heading text-2xl font-bold text-edupaws-navy">15</div>
              <div className="text-sm text-gray-500 font-body">Possible Results</div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Email Gate Screen ──
  if (step === 10) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <header className="py-4 px-6 flex items-center justify-between max-w-5xl mx-auto w-full">
          <button onClick={() => setStep(9)}
            className="flex items-center gap-2 text-gray-400 hover:text-edupaws-navy transition-colors font-body text-sm">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <img src="https://edupaws.co/cdn/shop/files/Edu_Paws_Logo_Updated.png?v=1759285352&width=100" alt="Edu Paws" className="h-10" />
        </header>

        <div className="max-w-5xl mx-auto w-full px-6">
          <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div className="h-full bg-edupaws-sage rounded-full transition-all duration-500" style={{ width: '100%' }} />
          </div>
        </div>

        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <div className="max-w-md w-full text-center">
            <div className="w-20 h-20 bg-edupaws-sage/15 rounded-3xl flex items-center justify-center mx-auto mb-8">
              <Sparkles className="w-10 h-10 text-edupaws-sage" />
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-edupaws-navy mb-4 leading-tight">
              Your results are ready
            </h2>
            <p className="text-gray-500 font-body text-base mb-8 leading-relaxed">
              Enter your email to unlock your dog's psychological profile and your personalized action plan.
            </p>
            <form onSubmit={handleEmailGateSubmit} className="space-y-4">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="your@email.com"
                required
                data-testid="quiz-email-gate-input"
                className="h-14 text-center text-lg rounded-2xl border-2 border-gray-200 focus:border-edupaws-sage font-body"
              />
              <button
                type="submit"
                disabled={submitting}
                data-testid="quiz-email-gate-submit"
                className="w-full inline-flex items-center justify-center gap-3 bg-edupaws-navy text-white font-heading font-bold text-lg py-4 rounded-full
                           shadow-[0_4px_0_#2D3B47] hover:shadow-[0_2px_0_#2D3B47] hover:translate-y-[2px] transition-all disabled:opacity-50"
              >
                {submitting ? 'Calculating...' : 'See My Results'}
                {!submitting && <ArrowRight className="w-5 h-5" />}
              </button>
            </form>
            <p className="text-xs text-gray-400 font-body mt-6">
              We respect your privacy. No spam, ever.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ── Result Screen ──
  if (step === 11 && result) {
    const nature = DOG_NATURES[result.dogNature];
    const pattern = HUMAN_PATTERNS[result.humanPattern];
    const recommended = PRICING[result.bestMatch];
    const allTiers = ['Full Package', 'Accelerated', 'Monthly'];

    return (
      <div className="min-h-screen bg-white">
        <header className="py-4 px-6 flex items-center justify-between max-w-5xl mx-auto">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-500 hover:text-edupaws-navy transition-colors font-body">
            <ArrowLeft className="w-5 h-5" /> Home
          </button>
          <img src="https://edupaws.co/cdn/shop/files/Edu_Paws_Logo_Updated.png?v=1759285352&width=100" alt="Edu Paws" className="h-10" />
        </header>

        <div className="max-w-3xl mx-auto px-6 py-10">
          {/* Q9 Opener */}
          {result.q9Text && (
            <p className="text-center text-gray-500 font-body italic text-lg mb-6" data-testid="q9-opener">
              You came here because: <span className="text-edupaws-navy font-semibold not-italic">"{result.q9Text}"</span>
              <br />Here's what your answers actually reveal.
            </p>
          )}

          {/* Dog Nature Result */}
          <div className="bg-edupaws-navy rounded-3xl p-8 md:p-10 text-white mb-8" data-testid="dog-nature-result">
            <div className="flex items-center gap-3 mb-2">
              <PawPrint className="w-6 h-6 text-edupaws-sage" />
              <span className="text-edupaws-sage font-body font-bold text-sm uppercase tracking-wider">Your Dog's True Nature</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold mb-2">{nature.title}</h2>
            <p className="text-white/70 font-body italic text-lg mb-6">{nature.tagline}</p>
            <p className="text-white/90 font-body text-base leading-relaxed mb-6">{nature.description}</p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="bg-white/10 rounded-2xl p-4">
                <div className="text-edupaws-sage font-body font-bold text-sm mb-1">Strengths</div>
                <p className="text-white/80 font-body text-sm">{nature.strengths}</p>
              </div>
              <div className="bg-white/10 rounded-2xl p-4">
                <div className="text-edupaws-sage font-body font-bold text-sm mb-1">The Real Work</div>
                <p className="text-white/80 font-body text-sm">{nature.challenge}</p>
              </div>
            </div>
          </div>

          {/* Human Pattern Result */}
          <div className="bg-edupaws-cream rounded-3xl p-8 md:p-10 mb-8" data-testid="human-pattern-result">
            <div className="flex items-center gap-3 mb-2">
              <Sparkles className="w-6 h-6 text-edupaws-sage" />
              <span className="text-edupaws-sage font-body font-bold text-sm uppercase tracking-wider">Your Pattern</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl font-extrabold text-edupaws-navy mb-2">{pattern.title}</h2>
            <p className="text-gray-500 font-body italic text-lg mb-6">{pattern.tagline}</p>
            <p className="text-gray-700 font-body text-base leading-relaxed">{pattern.description}</p>
          </div>

          {/* Path Forward, Pricing */}
          <div className="mb-10" data-testid="path-forward">
            <h3 className="font-heading text-2xl font-extrabold text-edupaws-navy text-center mb-2">Your Path Forward</h3>
            <p className="text-center text-gray-500 font-body mb-8">
              Based on your {nature.title.replace('The ', '')} + {pattern.title.replace('The ', '')} combination, we recommend:
            </p>

            <div className="grid md:grid-cols-3 gap-4">
              {allTiers.map(tier => {
                const p = PRICING[tier];
                const isRecommended = tier === result.bestMatch;
                return (
                  <div key={tier}
                    data-testid={`pricing-${tier.toLowerCase().replace(/\s/g, '-')}`}
                    className={`rounded-2xl p-6 border-2 transition-all relative
                      ${isRecommended
                        ? 'border-edupaws-sage bg-edupaws-sage/5 shadow-lg scale-[1.02]'
                        : 'border-gray-200 bg-white'}`}
                  >
                    {isRecommended && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-edupaws-sage text-white text-xs font-body font-bold px-4 py-1 rounded-full">
                        Best Match
                      </div>
                    )}
                    <h4 className="font-heading text-lg font-bold text-edupaws-navy mb-1">{tier}</h4>
                    <div className="flex items-baseline gap-1 mb-1">
                      <span className="font-heading text-3xl font-extrabold text-edupaws-navy">{p.price}</span>
                      <span className="text-gray-500 font-body text-sm">{p.period}</span>
                    </div>
                    {p.save && <span className="inline-block bg-edupaws-sage/20 text-edupaws-sage text-xs font-body font-bold px-2 py-0.5 rounded-full mb-3">{p.save}</span>}
                    <p className="text-gray-500 font-body text-sm mt-2">{p.desc}</p>
                    <button
                      onClick={() => navigate(`/signup?tier=${tier.toLowerCase().replace(/\s/g, '-')}`)}
                      className={`w-full mt-4 py-2.5 rounded-full font-body font-bold text-sm transition-all
                        ${isRecommended
                          ? 'bg-edupaws-navy text-white shadow-[0_3px_0_#2D3B47] hover:shadow-[0_1px_0_#2D3B47] hover:translate-y-[2px]'
                          : 'bg-gray-100 text-edupaws-navy hover:bg-gray-200'}`}
                    >
                      Get Started
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Retake / Home */}
          <div className="flex gap-4 justify-center">
            <button onClick={() => { setStep(0); setAnswers({}); setResult(null); setEmailSaved(false); setQuizId(null); setEmail(''); setPendingAnswers(null); }}
              className="font-body font-bold text-gray-500 hover:text-edupaws-navy transition-colors">
              Retake Quiz
            </button>
            <button onClick={() => navigate('/')}
              className="font-body font-bold text-edupaws-sage hover:text-edupaws-navy transition-colors">
              Back to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Question Screen ──
  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header */}
      <header className="py-4 px-6 flex items-center justify-between max-w-5xl mx-auto w-full">
        <button onClick={() => step > 1 ? setStep(step - 1) : setStep(0)}
          className="flex items-center gap-2 text-gray-400 hover:text-edupaws-navy transition-colors font-body text-sm">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>
        <span className="text-sm text-gray-400 font-body">
          {step} of {totalQuestions}
        </span>
      </header>

      {/* Progress Bar */}
      <div className="max-w-5xl mx-auto w-full px-6">
        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-edupaws-sage rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>
      </div>

      {/* Question */}
      {currentQuestion && (
        <div className="flex-1 flex items-center justify-center px-6 py-10">
          <div className={`max-w-2xl w-full transition-opacity duration-300 ${animating ? 'opacity-0' : 'opacity-100'}`}>
            {/* Part Label */}
            <div className="flex items-center gap-2 mb-6">
              <span className={`text-xs font-body font-bold uppercase tracking-wider px-3 py-1 rounded-full
                ${currentQuestion.part === 1 ? 'bg-edupaws-navy/10 text-edupaws-navy' :
                  currentQuestion.part === 2 ? 'bg-edupaws-sage/15 text-edupaws-sage' :
                  'bg-amber-100 text-amber-700'}`}>
                Part {currentQuestion.part}: {currentQuestion.partLabel}
              </span>
              {currentQuestion.optional && (
                <span className="text-xs font-body text-gray-400">(optional)</span>
              )}
            </div>

            {/* Question Text */}
            <h2 className="font-heading text-2xl sm:text-3xl font-extrabold text-edupaws-navy mb-8 leading-snug">
              {currentQuestion.question}
            </h2>

            {/* Options */}
            <div className="space-y-3">
              {currentQuestion.options.map(opt => {
                const isSelected = answers[currentQuestion.id] === opt.label;
                return (
                  <button
                    key={opt.label}
                    onClick={() => handleAnswer(currentQuestion.id, opt.label)}
                    data-testid={`option-${currentQuestion.id}-${opt.label.toLowerCase()}`}
                    className={`w-full text-left p-5 rounded-2xl border-2 transition-all font-body text-base leading-relaxed
                      ${isSelected
                        ? 'border-edupaws-sage bg-edupaws-sage/10 text-edupaws-navy'
                        : 'border-gray-200 hover:border-edupaws-sage/50 hover:bg-gray-50 text-gray-700'}`}
                  >
                    <span className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-gray-100 text-edupaws-navy font-bold text-sm mr-3 flex-shrink-0">
                      {opt.label}
                    </span>
                    {opt.text}
                  </button>
                );
              })}
            </div>

            {/* Skip for optional Q9 */}
            {currentQuestion.optional && (
              <button
                onClick={skipOptional}
                data-testid="skip-q9"
                className="mt-6 text-gray-400 font-body text-sm hover:text-gray-600 transition-colors mx-auto block"
              >
                Skip this question
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
